import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import {
  UploadCloud, FileText, Loader2, CheckCircle2, AlertTriangle, Search, MapPin,
} from "lucide-react";
import { toast } from "sonner";

function fmt(n) { return (n ?? 0).toLocaleString("en-US"); }

export default function AudienceCreate() {
  const [tab, setTab] = useState("single");

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1400px]">
      <PageHeader
        eyebrow="New audience"
        title="Run an audience query"
        description="Send a coordinate or a full CSV through your audience source platform. Every run produces a card on the Audiences screen with a 4 × 5 matrix and a live map of every point."
        testid="audience-create-header"
      />

      <div className="border-b border-[#E0E0D8] flex gap-1 mb-8" data-testid="audience-create-tabs">
        <TabButton active={tab === "single"} onClick={() => setTab("single")} testid="tab-single" icon={Search}>
          Single query
        </TabButton>
        <TabButton active={tab === "bulk"} onClick={() => setTab("bulk")} testid="tab-bulk" icon={UploadCloud}>
          Bulk upload
        </TabButton>
      </div>

      {tab === "single" ? <SingleForm /> : <BulkForm />}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
        active ? "border-[#FF3E00] text-[#0A0A0A]" : "border-transparent text-[#555] hover:text-[#0A0A0A]"
      }`}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

/* ---------------- Single query ---------------- */
function SingleForm() {
  const navigate = useNavigate();
  const [lat, setLat] = useState("40.7128");
  const [lon, setLon] = useState("-74.0060");
  const [label, setLabel] = useState("Times Square");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/audience/query", {
        latitude: parseFloat(lat),
        longitude: parseFloat(lon),
        label: label || null,
      });
      toast.success("Audience generated");
      // Backend wraps the single query into a batch — navigate to its detail view.
      const batchListResp = await api.get("/audience/batches");
      const latest = batchListResp.data.find((b) => (b.kind || "bulk") === "single") || batchListResp.data[0];
      navigate(`/audiences/${latest?.id || data.query_id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="single-query-form" className="bg-white border border-[#E0E0D8] p-6 md:p-8">
      <SectionLabel>Coordinate input</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-3">
          <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Latitude</label>
          <input
            data-testid="single-lat-input" required type="number" step="any"
            value={lat} onChange={(e) => setLat(e.target.value)}
            className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Longitude</label>
          <input
            data-testid="single-lon-input" required type="number" step="any"
            value={lon} onChange={(e) => setLon(e.target.value)}
            className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
          />
        </div>
        <div className="md:col-span-4">
          <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">Label (optional)</label>
          <input
            data-testid="single-label-input" type="text"
            value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Flagship store"
            className="mt-1 w-full bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
          />
        </div>
        <div className="md:col-span-2 flex items-end">
          <button
            type="submit" disabled={submitting}
            data-testid="single-submit-button"
            className="w-full h-[42px] bg-[#FF3E00] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#D93500] transition-colors disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <MapPin size={16} />}
            {submitting ? "Querying" : "Run query"}
          </button>
        </div>
      </div>
      {error && <p data-testid="single-error" className="mt-4 text-xs font-mono text-[#D91919]">{error}</p>}
      <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.15em] text-[#888]">
        After running you&apos;ll be taken to the audience card with full matrix and map.
      </p>
    </form>
  );
}

/* ---------------- Bulk upload ---------------- */
function BulkForm() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [error, setError] = useState("");

  const validateAndSet = (f) => {
    setError("");
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setError("Only .csv files are supported.");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setError(""); setBatch(null);
    try {
      const form = new FormData();
      form.append("file", file);
      // Let axios/browser set the multipart Content-Type with proper boundary.
      const { data } = await api.post("/audience/upload", form);
      setBatch(data);
      toast.success(`Processed ${data.processed} of ${data.total_rows} rows`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const downloadSample = () => {
    const sample = `latitude,longitude,label
40.7128,-74.0060,NYC - Times Square
34.0522,-118.2437,LA - Downtown
41.8781,-87.6298,Chicago - Loop
37.7749,-122.4194,SF - Union Square
51.5074,-0.1278,London - Westminster`;
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "geosync_sample_coordinates.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={downloadSample}
          data-testid="download-sample-button"
          className="border border-[#0A0A0A] px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#0A0A0A] hover:text-white transition-colors"
        >
          Sample CSV
        </button>
      </div>

      <div
        data-testid="dropzone"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); validateAndSet(e.dataTransfer.files?.[0]); }}
        className="border-2 border-dashed border-[#0A0A0A] bg-[#F4F4F0] p-12 md:p-20 text-center hover:border-[#FF3E00] hover:bg-[#FFF5F2] transition-colors cursor-pointer flex flex-col items-center justify-center gap-4"
      >
        <UploadCloud size={48} strokeWidth={1.25} className="text-[#0A0A0A]" />
        <p className="text-2xl font-bold tracking-tight">{file ? file.name : "Drop CSV or click to browse"}</p>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#555]">
          Required columns: latitude, longitude · Optional: label
        </p>
        <input
          ref={fileRef} data-testid="file-input"
          type="file" accept=".csv" className="hidden"
          onChange={(e) => validateAndSet(e.target.files?.[0])}
        />
      </div>

      {error && (
        <p data-testid="upload-error" className="text-xs font-mono text-[#D91919] border border-[#D91919]/30 bg-[#D91919]/5 px-3 py-2 inline-block">
          {error}
        </p>
      )}

      {file && (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border border-[#E0E0D8] bg-white p-4">
          <div className="flex items-center gap-3">
            <FileText size={20} />
            <div>
              <p className="text-sm font-semibold">{file.name}</p>
              <p className="text-[11px] font-mono uppercase tracking-wider text-[#555]">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          </div>
          <button
            onClick={upload} disabled={uploading}
            data-testid="upload-submit-button"
            className="bg-[#FF3E00] text-white px-6 py-3 font-semibold hover:bg-[#D93500] transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {uploading ? "Processing rows" : "Process file"}
          </button>
        </div>
      )}

      {batch && (
        <section data-testid="batch-results">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Stat label="Total rows" value={fmt(batch.total_rows)} />
            <Stat label="Processed" value={fmt(batch.processed)} tone="ok" />
            <Stat label="Failed" value={fmt(batch.failed)} tone={batch.failed > 0 ? "err" : ""} />
          </div>

          <div className="flex items-center justify-between mb-3">
            <SectionLabel>Batch ID · {batch.batch_id}</SectionLabel>
            <button
              onClick={() => navigate(`/audiences/${batch.batch_id}`)}
              data-testid="open-batch-button"
              className="bg-[#0A0A0A] text-white px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#FF3E00] transition-colors"
            >
              Open audience card →
            </button>
          </div>

          <div className="border border-[#E0E0D8] bg-white overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A]">
                  <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Label</th>
                  <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Lat / Lon</th>
                  <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">7d · 1km</th>
                  <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">30d · 1km</th>
                  <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">90d · 1km</th>
                  <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Status</th>
                </tr>
              </thead>
              <tbody>
                {batch.results.map((r) => {
                  const find = (rad, win) => r.cells.find((c) => c.radius_m === rad && c.time_window_days === win)?.count ?? 0;
                  return (
                    <tr key={r.id} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0]">
                      <td className="py-3 px-4 text-sm">{r.label || "—"}</td>
                      <td className="py-3 px-4 font-mono text-xs">{r.latitude.toFixed(4)}, {r.longitude.toFixed(4)}</td>
                      <td className="py-3 px-4 text-right font-mono">{fmt(find(1000, 7))}</td>
                      <td className="py-3 px-4 text-right font-mono">{fmt(find(1000, 30))}</td>
                      <td className="py-3 px-4 text-right font-mono">{fmt(find(1000, 90))}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[#00994D]">
                          <CheckCircle2 size={12} /> {r.source_status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {batch.failed > 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 px-4 text-xs font-mono text-[#D91919] flex items-center gap-2">
                      <AlertTriangle size={14} /> {batch.failed} row(s) failed validation
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "ok" ? "text-[#00994D]" : tone === "err" ? "text-[#D91919]" : "text-[#0A0A0A]";
  return (
    <div className="border border-[#E0E0D8] bg-white p-5">
      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#555]">{label}</p>
      <p className={`font-mono text-3xl tracking-tighter mt-2 ${color}`}>{value}</p>
    </div>
  );
}
